/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import { observer } from "mobx-react-lite"
import { useId } from "react"
import { generatePath, Link } from "react-router"

import type { ApplicationType } from "../../features/application-type/application-type-types"
import { AppRoutes } from "../../routes/routes"
import { valueOrDash } from "../../utils/value-or-dash"
import { AppIcon } from "../components/app-icon"
import { CategoryPlatform } from "../components/category-platform"
import { TableCell, TableRow } from "../primitives/table"

import styles from "./application-type-row.module.css"

interface Props {
    /**
     * Application to display. Missing text fields show an em dash.
     */
    appType: ApplicationType
}

/**
 * Renders an application as a table row with its icon, description, package
 * name, category and platform.
 *
 * The description is a real link, and the row's CSS module stretches it over
 * the whole row. Clicking anywhere opens the application detail, a middle or
 * Ctrl/Cmd click opens it in a new tab, and the row stays a single tab stop.
 */
export const ApplicationTypeRow = observer(({ appType }: Props) => {
    const packageNameId = useId()
    const categoryPlatformId = useId()

    return (
        <TableRow className={styles.row}>
            {/*
             * `w-0` shrinks the icon and badge columns to fit their content. `w-full max-w-0`
             * gives the remaining width to the name column, so long names truncate instead
             * of widening the table.
             */}
            <TableCell className="w-0">
                <AppIcon appTypeId={appType.id} />
            </TableCell>
            <TableCell className="w-full max-w-0">
                {/*
                 * The Android and Apple versions of an app often share a name. Describe
                 * the link with the package name, category and platform, so screen reader
                 * users can tell the versions apart.
                 */}
                <Link
                    to={generatePath(AppRoutes.ApplicationTypeDetail, { id: `${appType.id}` })}
                    aria-describedby={`${packageNameId} ${categoryPlatformId}`}
                    className={clsx("block truncate text-base font-semibold", styles.link)}
                >
                    {valueOrDash(appType.description)}
                </Link>
                <div id={packageNameId} className="truncate text-muted-foreground">
                    {valueOrDash(appType.sourcePackageName)}
                </div>
            </TableCell>
            <TableCell id={categoryPlatformId} className="w-0">
                <CategoryPlatform category={appType.category} platform={appType.platform} />
            </TableCell>
        </TableRow>
    )
})
